import { useContext, useState } from "react";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { cleanObject, getRandomIntInclusive } from "../../../helpers";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import { resolveAccountNumber } from "../../../services/transfers";
import {
  useCreateExpenseMutation,
  useUpdateExpense,
} from "../../../hooks/api/mutations/expenses";
import {
  useGetExpenseCategories,
  useGetExpenseDepartments,
} from "../../../hooks/api/queries/useExpenses";
import { useGetBankNameList } from "../../../hooks/api/queries";
import { useUploadImageMutation } from "../../../hooks/api/mutations/useMedia";

const useCreateExpense = ({ isEditMode, expenseInfo, closeWidget }) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const [expenseDetails, setExpenseDetails] = useState({
    name: expenseInfo?.name || "",
    store: expenseInfo?.store?.id || "",
    amount: expenseInfo?.amount || "",
    vat_rate: isEditMode ? expenseInfo?.vat_rate : expenseInfo?.vat_rate || 7.5,
    category: expenseInfo?.category?.id || "",
    payment_method: expenseInfo?.payment_method || "",
    description: expenseInfo?.description,
    expense_date: expenseInfo?.expense_date,
    proof_url: expenseInfo?.proof_url,
    bank_code: expenseInfo?.bank_code || "",
    account_number: expenseInfo?.account_number || "",
    account_name: expenseInfo?.account_name || "",
    department: expenseInfo?.department?.id || "",
    make_refund:
      expenseInfo?.account_number && expenseInfo?.account_number !== ""
        ? true
        : false,
    is_recurring: expenseInfo?.is_recurring || false,
    recurrence_frequency: expenseInfo?.recurrence_frequency || "",
    recurrence_start_date: expenseInfo?.recurrence_start_date || "",
  });
  const [chargeVat, setChargeVat] = useState(expenseInfo?.vat_rate !== 0);

  const { data, isLoading: isLoadingDepartments } = useGetExpenseDepartments(
    {},
  );
  const { data: expenseCategories = [], isLoading: isLoadingCategories } =
    useGetExpenseCategories();
  const { data: bankList = [] } = useGetBankNameList({
    isEnabled: expenseDetails?.make_refund,
  });

  const { handleUploadMedia, isLoading: isLoadingDocUpload } =
    useUploadImageMutation({
      onSuccess: (data) => {
        setExpenseDetails((prev) => ({
          ...prev,
          proof_url: data?.url,
          file_name: data?.file_name,
        }));
      },
    });

  const { mutate: resolveAccount } = useMutation((data) =>
    resolveAccountNumber(data),
  );
  const handleResolveAccountNumber = (bankCode, accountNumber) => {
    resolveAccount(
      { bank_code: bankCode, account_number: accountNumber },
      {
        onSuccess: (res) => {
          setExpenseDetails((prevState) => ({
            ...prevState,
            account_name: res?.data?.data?.account_name,
          }));
        },
        onError: () => {
          setExpenseDetails((prevState) => ({
            ...prevState,
            account_name: null,
          }));
        },
      },
    );
  };

  const { mutate: addExpense, isLoading: isCreatingExpense } =
    useCreateExpenseMutation();
  const handleCreateExpense = () => {
    setRequestLoaderProgress(getRandomIntInclusive(5, 40));
    addExpense(cleanObject({ ...expenseDetails }), {
      onSuccess: () => {
        triggerToast("Expense creation successful", "success");
        closeWidget();
      },

      onSettled: () => setRequestLoaderProgress(100),
    });
  };

  const { mutate: updateExpense, isLoading: isUpdatingExpense } =
    useUpdateExpense({ id: expenseInfo?.id });
  const handleUpdateExpense = () => {
    setRequestLoaderProgress(getRandomIntInclusive(5, 40));
    updateExpense(
      { id: expenseInfo?.id, data: cleanObject(expenseDetails) },
      {
        onSuccess: () => {
          triggerToast("Expense Updated successfully", "success");
          closeWidget();
        },

        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  const handleSubmitExpense = () => {
    if (isEditMode) return handleUpdateExpense();

    handleCreateExpense();
  };

  return {
    isLoadingDocUpload,
    isLoading: isLoadingCategories || isLoadingDepartments,
    bankList,
    expenseDepartments: data?.data || [],
    expenseCategories,
    storeList,
    expenseDetails,
    isCreatingExpense: isCreatingExpense || isUpdatingExpense,
    chargeVat,
    setExpenseDetails,
    handleSubmitExpense,
    handleUploadMedia,
    handleResolveAccountNumber,
    setChargeVat,
  };
};
export default useCreateExpense;
